import type { CustomerStatus, Organization, OrganizationListItem, PersonListItem } from './index';

export type ListKind = 'MANUAL' | 'SMART';
export type ListEntityKind = 'SCHOOL' | 'CLUB_TEAM' | 'PERSON' | 'PROGRAM' | 'ORG';
export type SharePermission = 'VIEW' | 'EDIT';
export type ShareGranteeType = 'USER' | 'TEAM';

export interface FilterSpec {
  version: 1;
  search?: string | null;
  states?: string[];
  ncesLevels?: string[];
  customerStatus?: string[];
  hasBlastLink?: boolean;
  enrollmentMin?: number;
  enrollmentMax?: number;
  isPrivate?: boolean;
  stages?: string[];
  temperatures?: string[];
  role?: string;
  sport?: string;
  inBlast?: boolean;
  excludeDnc?: boolean;
  sports?: string[];
  levels?: string[];
  // grid extensions
  orgTypes?: string[];
  assigned?: boolean;
  lastActivityFrom?: string;
  lastActivityTo?: string;
  lastVisitFrom?: string;
  lastVisitTo?: string;
  genders?: string[];
  seasons?: string[];
  rosterMin?: number;
  rosterMax?: number;
  hasActiveStore?: boolean;
  tagIds?: string[];
  tagMode?: 'any' | 'all';
  channels?: string[];
}

export const PERSON_SOURCE_CHANNELS = ['CLELL_WADE', 'BLAST', 'MANUAL', 'IMPORT'] as const;
export const CHANNEL_LABELS: Record<string, string> = {
  CLELL_WADE: 'Clell Wade',
  BLAST: 'Blast',
  MANUAL: 'Manual',
  IMPORT: 'Import',
};

export interface SortSpec { field: string; dir: 'asc' | 'desc'; }

export interface ListShare {
  id: string;
  listId: string;
  granteeType: ShareGranteeType;
  granteeUserId: string | null;
  permission: SharePermission;
  grantee?: { id: string; displayName: string | null; email: string } | null;
}

export interface List {
  id: string;
  ownerId: string;
  name: string;
  description?: string | null;
  color?: string | null;
  kind: ListKind;
  isSystem: boolean;
  systemKey?: string | null;
  smartEntityKind?: ListEntityKind | null;
  filterSpec?: FilterSpec | null;
  sortField?: string | null;
  sortDir?: 'asc' | 'desc' | null;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  shares?: ListShare[];
  owner?: { id: string; displayName: string | null };
  count?: number;
  kindCounts?: { SCHOOL: number; CLUB_TEAM: number; PERSON: number; PROGRAM: number } | null;
}

// A program (Team) as hydrated by the manual-items endpoint: a Team has NO name/org of its own —
// its label is `level`, and its school is reached via sport.organization.
export interface ProgramView {
  id: string;
  level: string | null;
  isActive?: boolean;
  rosterCount?: number | null;
  sport?: { id: string; name: string; season?: string | null; hasActiveStore?: boolean; organization?: (Organization & { customerStatus?: CustomerStatus }) | null } | null;
}

// organization/person use the *card-consumed* list shapes so the polymorphic
// ListItemCard (a later task) can pass them straight into OrganizationCard /
// PersonCard with no cast. NOTE for later tasks: the backend GET /api/lists/:id/items
// endpoint hydrates BARE rows (no assignedRep, no person.organizations, no coachCount
// aggregate) — both cards already guard for those, so list items render in a graceful
// degraded state. This is an accepted Part-1 tradeoff.
export interface ListItemView {
  id?: string;                 // present for manual items
  kind: ListEntityKind;
  rank?: number | null;
  available?: boolean;         // manual: whether the target is still active
  organization?: OrganizationListItem | null;
  person?: PersonListItem | null;
  team?: ProgramView | null;
  isFavorite: boolean;
  lastActivityBy?: string | null;
}

export interface FavoriteIds {
  schools: (string | null)[];
  clubTeams: (string | null)[];
  people: (string | null)[];
  programs: (string | null)[];
}
